/**
 * Pequeñas Puntadas — customer-facing content and image mapping.
 *
 * Every image has a descriptive `file` name. While `src` is empty, the page
 * shows a prepared frame with that file name; paste the uploaded image URL
 * into `src` to replace it.
 */

export interface SiteImage {
  src: string
  file: string
  alt: string
  width: number
  height: number
  /** Optional lighter file for thumbnails; the full-resolution `src` is used to enlarge. */
  thumbSrc?: string
}

export interface ManualPage extends SiteImage {
  caption: string
}

export interface InteriorSample extends SiteImage {
  title: string
  volume: string
  model: string
  description: string
}

export interface Volume {
  number: number
  summary: string
  models: string[]
  cover: SiteImage
}

export interface Bonus {
  number: number
  title: string
  benefit: string
  /** Reference value shown struck through; bonuses are included at no extra cost. */
  compareAt: string
  cover: SiteImage
}

export interface Faq {
  question: string
  answer: string
}

export const BRAND = {
  name: 'Pequeñas Puntadas™',
  descriptor: 'Moldes Multi-talle de ropa infantil para coser, regalar o vender',
} as const

export const OFFER_ANCHOR = '#oferta'
export const PREVIEW_ANCHOR = '#modelo'

export const GALLERY_ANCHOR = '#prendas'

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Prendas', href: '#prendas' },
  { label: 'Por dentro', href: '#modelo' },
  { label: 'Bonos', href: '#bonos' },
  { label: 'Volúmenes', href: '#volumenes' },
  { label: 'Preguntas', href: '#preguntas' },
]


/* ——— Finished-garment gallery (digital visualizations, not customer results) ——— */

export interface Garment {
  title: string
  caption: string
  image: SiteImage
}

export const GARMENTS: Garment[] = [
  {
    title: 'Vestido con cuello y lazo',
    caption: 'Modelo V2-02 · Volumen 2',
    image: {
      src: 'https://cdn.magicpatterns.com/uploads/tFDF6VbWAJEueQdFhA5gWz/modelo_3.png',
      file: 'modelo_3.png',
      alt: 'Visualización digital de un vestido rosa viejo con cuello, mangas cortas, falda fruncida y lazos a los costados',
      width: 1254,
      height: 1254,
    },
  },
  {
    title: 'Jardinero corto',
    caption: 'Inspirado en el Jardinero corto de pechera sencilla · Volumen 1',
    image: {
      src: 'https://cdn.magicpatterns.com/uploads/dojgzQDwW1qsqAZWVHtwBt/modelo_1.png',
      file: 'modelo_1.png',
      alt: 'Visualización digital de un jardinero corto verde salvia con tiradores y cintura elastizada',
      width: 1254,
      height: 1254,
    },
  },
  {
    title: 'Ranita floral',
    caption: 'Inspirada en el modelo V5-02 · Volumen 5',
    image: {
      src: 'https://cdn.magicpatterns.com/uploads/wxVvELMsZYhpt9NRWuayNw/modelo_2.png',
      file: 'modelo_2.png',
      alt: 'Visualización digital de una ranita floral color crema con tiradores cruzados',
      width: 1254,
      height: 1254,
    },
  },
]

export const GALLERY_NOTE =
  'Visualizaciones ilustrativas de los diseños. Las telas, colores y terminaciones pueden variar según tu confección.'

/* ——— Sample model: Volumen 2 · Vestido con cuello y lazo ——— */

export const SAMPLE_MODEL = {
  name: 'Vestido con cuello y lazo',
  volume: 'Volumen 2',
} as const

/** Real manual pages (letter-like ratio) and pattern pages (A4 ratio). */
const MANUAL_SIZE = { width: 1224, height: 1584 }
const PATTERN_SIZE = { width: 1190, height: 1684 }

const MANUAL_ASSETS: Array<{ src: string; caption: string; page: number }> = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/hMM74dYQypbrTpTSQLMFPM/v2-vestido-con-cuello-y-lazo-manual-pag-01.png',
    caption: 'Presentación del modelo',
    page: 9,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/oCzAH9vcojEq7TXsye93Nw/v2-vestido-con-cuello-y-lazo-manual-pag-02.png',
    caption: 'Inventario de piezas',
    page: 10,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/cxvMfwa9xXF7ML6dtoaKNu/v2-vestido-con-cuello-y-lazo-manual-pag-03.png',
    caption: 'Preparar y cortar',
    page: 11,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/95Xmo34oEA3TYB25FhUEzY/v2-vestido-con-cuello-y-lazo-manual-pag-04.png',
    caption: 'Armado: primera parte',
    page: 12,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/32Vp6Zn7x2aWmMaoFZ3ZHS/v2-vestido-con-cuello-y-lazo-manual-pag-05.png',
    caption: 'Armado: segunda parte',
    page: 13,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/uMNM2fAxGYscQqhUVpvJ4o/v2-vestido-con-cuello-y-lazo-manual-pag-06.png',
    caption: 'Terminaciones y control',
    page: 14,
  },
]

export const MANUAL_PAGES: ManualPage[] = MANUAL_ASSETS.map(({ src, caption, page }, index) => ({
  src,
  file: `v2-vestido-con-cuello-y-lazo-manual-pag-${String(index + 1).padStart(2, '0')}.png`,
  alt: `Manual del Volumen 2, página ${page}: V2-02 Vestido con cuello y lazo — ${caption}`,
  caption,
  ...MANUAL_SIZE,
}))

export const PATTERN_PAGES: ManualPage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/i2NBUjm1SKehZw9rJFKoD2/v2-vestido-con-cuello-y-lazo-molde-mapa.png',
    file: 'v2-vestido-con-cuello-y-lazo-molde-mapa.png',
    alt: 'PDF de moldes del Volumen 2, página 37: mapa de hojas del V2-02 Vestido con cuello y lazo',
    caption: 'Esquema de armado · pág. 37',
    ...PATTERN_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/aKAfKAtXuzrpG9ULHLbSgR/v2-vestido-con-cuello-y-lazo-molde-a1.png',
    file: 'v2-vestido-con-cuello-y-lazo-molde-a1.png',
    alt: 'PDF de moldes del Volumen 2, página 38: hoja A1 del V2-02 Vestido con cuello y lazo, con las líneas de los diez talles',
    caption: 'Hoja A1 · pág. 38',
    ...PATTERN_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/wWdSzNzYwUpyqCHKfRSbaw/v2-vestido-con-cuello-y-lazo-molde-a2.png',
    file: 'v2-vestido-con-cuello-y-lazo-molde-a2.png',
    alt: 'PDF de moldes del Volumen 2, página 39: hoja A2 del V2-02 Vestido con cuello y lazo, con las líneas de los diez talles',
    caption: 'Hoja A2 · pág. 39',
    ...PATTERN_SIZE,
  },
]

/* ——— Interior pages from different models ——— */

export const INTERIOR_SAMPLES: InteriorSample[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/i1ofipxdgqXdLuDJpq4jz1/interior-v5-ranita-floral-presentacion.png',
    file: 'interior-v5-ranita-floral-presentacion.png',
    alt: 'Manual del Volumen 5, página 9: presentación del modelo V5-02 Ranita floral con gorrito',
    title: 'Presentación del modelo',
    volume: 'Volumen 5',
    model: 'V5-02 · Ranita floral con gorrito',
    description: 'Manual de confección, página 9.',
    ...MANUAL_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/i1RKCZjb9NXUH4adGqDkxp/interior-v4-buzo-piezas.png',
    file: 'interior-v4-buzo-piezas.png',
    alt: 'Manual del Volumen 4, página 16: inventario de piezas del modelo V4-03 Buzo con capucha y pantalón',
    title: 'Inventario de piezas',
    volume: 'Volumen 4',
    model: 'V4-03 · Buzo con capucha y pantalón',
    description: 'Manual de confección, página 16.',
    ...MANUAL_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/p5r31j58tCAYG6PSDDoGn8/interior-v3-blusa-confeccion.png',
    file: 'interior-v3-blusa-confeccion.png',
    alt: 'Manual del Volumen 3, página 12: armado, primera parte, del modelo V3-02 Blusa abullonada y bombachudo',
    title: 'Armado: primera parte',
    volume: 'Volumen 3',
    model: 'V3-02 · Blusa abullonada y bombachudo',
    description: 'Manual de confección, página 12.',
    ...MANUAL_SIZE,
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/219oSMFVAvi1Pic9QXgEzg/interior-v1-enterito-cruzado-molde.png',
    file: 'interior-v1-enterito-cruzado-molde.png',
    alt: 'PDF de moldes del Volumen 1, página 107: hoja A1 del molde V1-07 Enterito cruzado',
    title: 'Hoja real del molde',
    volume: 'Volumen 1',
    model: 'V1-07 · Enterito cruzado',
    description: 'PDF de moldes, página 107.',
    ...PATTERN_SIZE,
  },
]

/* ——— The six volumes ——— */

/** Real cover artwork, shown whole at its natural 4:5 proportion. */
const COVER_SIZE = { width: 1224, height: 1530 }

const COVER_ASSETS: Record<number, { src: string; title: string }> = {
  1: {
    src: 'https://cdn.magicpatterns.com/uploads/nHi3pfpfbVyq3PNSEYsM59/portada-volumen-01.png',
    title: 'Básicos, ranitas y enteritos',
  },
  2: {
    src: 'https://cdn.magicpatterns.com/uploads/rHam2jJvHUfshMWt84pVdQ/portada-volumen-02.png',
    title: 'Vestiditos para distintas ocasiones',
  },
  3: {
    src: 'https://cdn.magicpatterns.com/uploads/tHurUdF6Qs1zs5mPfhFxXS/portada-volumen-03.png',
    title: 'Blusas y conjuntos livianos',
  },
  4: {
    src: 'https://cdn.magicpatterns.com/uploads/sYYhhXS5s9YTQEU2k9u3EJ/portada-volumen-04.png',
    title: 'Conjuntos para salir y jugar',
  },
  5: {
    src: 'https://cdn.magicpatterns.com/uploads/4b5M7FBeoyGXxwaZUqkiiR/portada-volumen-05.png',
    title: 'Ranitas con detalles especiales',
  },
  6: {
    src: 'https://cdn.magicpatterns.com/uploads/dgdAE62mmZ4KmSHrtUdd43/portada-volumen-06.png',
    title: 'Enterito y complementos infantiles',
  },
}

const cover = (n: number): SiteImage => ({
  src: COVER_ASSETS[n].src,
  file: `portada-volumen-${String(n).padStart(2, '0')}.png`,
  alt: `Portada del Volumen ${n} de Pequeñas Puntadas™: ${COVER_ASSETS[n].title}`,
  ...COVER_SIZE,
})

/** Complete product mockup (hero + offer). Shown whole, never re-framed. */
export const COLLECTION_MOCKUP: SiteImage = {
  src: 'https://cdn.magicpatterns.com/uploads/wzpFZZMnPMeNd1PdSdzKcW/mockup-coleccion.png',
  file: 'mockup-coleccion.png',
  alt: 'Colección digital Pequeñas Puntadas™: las seis portadas de los volúmenes, una tablet con la página del Vestido con cuello y lazo y una hoja de molde A4 impresa',
  width: 1254,
  height: 1254,
}

export const VOLUMES: Volume[] = [
  {
    number: 1,
    summary: 'Jardinero, ranitas, enteritos y body.',
    cover: cover(1),
    models: [
      'Jardinero corto de pechera sencilla',
      'Ranita de tiradores cruzados',
      'Ranita con volados y puntilla',
      'Enterito con botones delanteros',
      'Enterito corto con cuello',
      'Body sin mangas con gorrito',
      'Enterito cruzado',
      'Enterito largo con pies',
    ],
  },
  {
    number: 2,
    summary: 'Vestidos con distintos cuellos, mangas y faldas.',
    cover: cover(2),
    models: [
      'Vestido de falda doble',
      'Vestido con cuello y lazo',
      'Vestido de breteles escalonado',
      'Vestido con mangas abullonadas',
      'Vestido de encaje con forro',
    ],
  },
  {
    number: 3,
    summary: 'Vestido, blusas y conjuntos de dos piezas.',
    cover: cover(3),
    models: [
      'Vestido con cuello adornado y botones',
      'Blusa abullonada y bombachudo',
      'Blusa de breteles y bombachudo',
      'Blusa con volado y short con tiradores',
      'Conjunto playero de blusa y short',
    ],
  },
  {
    number: 4,
    summary: 'Conjuntos, buzo con capucha y jardineros.',
    cover: cover(4),
    models: [
      'Vestidito con bombachudo y cofia',
      'Camisa y short con tiradores',
      'Buzo con capucha y pantalón',
      'Body camisero y jardinero largo',
      'Body polo y jardinero corto',
    ],
  },
  {
    number: 5,
    summary: 'Ranitas con faldita, volados y gorrito.',
    cover: cover(5),
    models: [
      'Ranita con faldita y moño',
      'Ranita floral con gorrito',
      'Ranita con mangas caídas',
      'Ranita con faldita de volados',
    ],
  },
  {
    number: 6,
    summary: 'Enterito, bombachudo y accesorios.',
    cover: cover(6),
    models: [
      'Enterito camisero con cierre frontal',
      'Bombachudo con volados escalonados',
      'Gorrito y babero bandana',
      'Pañal de tela con broches',
    ],
  },
]

/* ——— Bonuses ——— */

/** Square mockup images that already contain the complete cover. */
const BONUS_SIZE = { width: 1024, height: 1024 }

const bonus = (number: number, title: string, benefit: string, compareAt: string, src: string): Bonus => ({
  number,
  title,
  benefit,
  compareAt,
  cover: {
    src,
    file: `portada-bono-${String(number).padStart(2, '0')}.png`,
    alt: `Portada del bono ${number}: ${title}`,
    ...BONUS_SIZE,
  },
})

export const BONUSES: Bonus[] = [
  bonus(
    1,
    'Guía de impresión y armado de moldes A4™',
    'Para imprimir a escala real y unir las hojas sin perderte.',
    '$14.990',
    'https://cdn.magicpatterns.com/uploads/qbXcdsALiRiL8uVxX8iZrZ/portada-bono-01.png',
  ),
  bonus(
    2,
    'Cuaderno de medidas, talles y ajustes™',
    'Para anotar las medidas de cada chico y elegir el talle con más seguridad.',
    '$9.990',
    'https://cdn.magicpatterns.com/uploads/oq31hLM75FJEMUv6Z2EMXv/portada-bono-02.png',
  ),
  bonus(
    3,
    'Checklist de preparación y corte™',
    'Para tener tela, piezas y materiales listos antes de cortar.',
    '$7.990',
    'https://cdn.magicpatterns.com/uploads/4PgBLxgasBMYcb5dRWgcDu/portada-bono-03.png',
  ),
  bonus(
    4,
    'Guía práctica de telas y terminaciones™',
    'Para elegir telas y terminaciones según la prenda que vas a coser.',
    '$7.990',
    'https://cdn.magicpatterns.com/uploads/mjrSUEJ3GTodxqZTvYopvN/portada-bono-04.png',
  ),
  bonus(
    5,
    'Cuaderno de costos y precios de tus prendas™',
    'Para registrar tus costos y definir el precio de lo que vendés.',
    '$9.990',
    'https://cdn.magicpatterns.com/uploads/6VjaehdfenKTs6phmZejTQ/portada-bono-05.png',
  ),
]

/* ——— Offer ——— */

export const OFFER_FEATURES: string[] = [
  '31 modelos de ropa infantil',
  '6 manuales de confección',
  '6 PDF de moldes A4',
  '10 talles por estatura',
  '5 bonos incluidos de regalo',
]

/* ——— FAQ ——— */

export const FAQS: Faq[] = [
  {
    question: '¿Qué incluye la colección?',
    answer:
      '31 modelos distribuidos en seis volúmenes. Cada volumen tiene su manual de confección y su PDF de moldes A4, por separado: en total, 12 PDF principales. Además, sumás cinco bonos complementarios sin cargo adicional.',
  },
  {
    question: '¿Qué talles trae?',
    answer:
      'Diez talles por estatura: 50, 56, 62, 68, 74, 80, 86, 92, 98 y 104 cm. Los accesorios de cabeza, como gorritos y cofias, se eligen por contorno de cabeza.',
  },
  {
    question: '¿Cómo elijo el talle?',
    answer:
      'Medí la estatura de la nena o el nene y elegí el talle más cercano. Si queda entre dos talles, compará también otras medidas del cuerpo antes de cortar. La edad sirve solo como referencia general: cada chico crece a su ritmo, por eso conviene guiarse por la estatura y las medidas.',
  },
  {
    question: '¿Puedo imprimir los moldes en casa?',
    answer:
      'Sí. Los moldes se imprimen en hojas A4, a escala real, con cualquier impresora hogareña. Imprimí al 100 % —sin ajustar a la página—, uní las hojas y recortá el talle que necesitás.',
  },
  {
    question: '¿Los moldes incluyen margen de costura?',
    answer: 'Sí. Los moldes ya incluyen un margen de costura de 7 mm.',
  },
  {
    question: '¿Recibo un producto físico?',
    answer:
      'No. Pequeñas Puntadas es una colección digital en PDF. No se envían libros, hojas impresas ni prendas: imprimís los moldes vos y consultás los manuales en tu dispositivo.',
  },
  {
    question: '¿Cómo accedo a los archivos?',
    answer:
      'Después del pago, accedés al enlace de descarga de todos los archivos. Te recomendamos guardarlos en tu dispositivo para tenerlos siempre a mano.',
  },
  {
    question: '¿Puedo consultar los manuales desde el celular?',
    answer:
      'Sí. Los manuales se consultan desde el celular, la tablet o la computadora. Los moldes, en cambio, están pensados para imprimir en A4.',
  },
  {
    question: '¿Necesito saber coser?',
    answer:
      'Conviene que tengas nociones básicas de costura y de uso de la máquina. Los manuales te acompañan en la confección de cada modelo, pero no son un curso en video ni un curso para aprender a coser desde cero.',
  },
  {
    question: '¿Puedo vender las prendas que confeccione?',
    answer:
      'Sí, podés vender las prendas que cosas con estos moldes. Lo que no está permitido es revender, compartir ni redistribuir los archivos PDF.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'
